import type {
    CommerceAddress,
    CommerceCheckout,
    CommerceCheckoutContact,
    CommerceOperationOptions,
    BeginCommerceQuickBuyRequest,
    CommerceAddressRegion,
    CommerceCheckoutPaymentConfiguration,
    CompleteCommerceCheckoutResponse,
    SubmitCommerceCheckoutResponse,
} from "@teqtank/commerce-contracts";
import { CommerceRepository } from "../repositories/commerce-repository.js";
import { beginCommerceCheckoutOperation } from "../operations/begin-commerce-checkout-operation.js";
import { calculateCommerceCheckoutOperation } from "../operations/calculate-commerce-checkout-operation.js";
import { getCommerceCheckoutOperation } from "../operations/get-commerce-checkout-operation.js";
import { selectCommerceCheckoutShippingMethodOperation } from "../operations/select-commerce-checkout-shipping-method-operation.js";
import { submitCommerceCheckoutOperation } from "../operations/submit-commerce-checkout-operation.js";
import { updateCommerceCheckoutAddressOperation } from "../operations/update-commerce-checkout-address-operation.js";
import { updateCommerceCheckoutContactOperation } from "../operations/update-commerce-checkout-contact-operation.js";
import { clearCommerceCheckoutCustomerDetailsOperation } from "../operations/clear-commerce-checkout-customer-details-operation.js";
import { beginCommerceQuickBuyOperation } from "../operations/begin-commerce-quick-buy-operation.js";
import { respondCommerceCheckoutUpsellOperation } from "../operations/respond-commerce-checkout-upsell-operation.js";
import { updateCommerceCheckoutItemPurchaseOperation } from "../operations/update-commerce-checkout-item-purchase-operation.js";
import { applyCommerceCheckoutCustomerOperation } from "../operations/apply-commerce-checkout-customer-operation.js";
import { selectCommerceCheckoutSavedPaymentMethodOperation } from "../operations/select-commerce-checkout-saved-payment-method-operation.js";
import { resolveCommerceCheckout } from "./checkout-response-resolver.js";
import { getCommerceAddressRegionsOperation } from "../operations/get-commerce-address-regions-operation.js";
import { getCommerceCheckoutPaymentConfigurationOperation } from "../operations/get-commerce-checkout-payment-configuration-operation.js";
import { selectCommerceCheckoutPaymentOptionOperation } from "../operations/select-commerce-checkout-payment-option-operation.js";
import { applyCommerceCheckoutPointsOperation } from "../operations/apply-commerce-checkout-points-operation.js";
import { completeCommerceCheckoutOperation } from "../operations/complete-commerce-checkout-operation.js";
import { resolveCommerceOrder } from "../orders/order-response-resolver.js";

export class CommerceCheckoutRepository extends CommerceRepository {
    public async getAddressRegions(
        countryCode: string,
        options: CommerceOperationOptions,
    ): Promise<readonly CommerceAddressRegion[]> {
        const response = await this.execute(
            getCommerceAddressRegionsOperation,
            { countryCode },
            options,
            "checkout-operation-failed",
            "Teqtank Commerce could not load address regions.",
        );
        return Object.freeze(response.regions.map((region) => Object.freeze({ ...region })));
    }

    public async beginQuickBuy(
        request: BeginCommerceQuickBuyRequest,
        options: CommerceOperationOptions,
    ): Promise<CommerceCheckout> {
        return resolveCommerceCheckout(
            (
                await this.execute(
                    beginCommerceQuickBuyOperation,
                    request,
                    options,
                    "checkout-operation-failed",
                    "Teqtank Commerce could not begin Quick Buy.",
                )
            ).checkout,
        );
    }

    public async begin(options: CommerceOperationOptions): Promise<CommerceCheckout> {
        return resolveCommerceCheckout(
            (
                await this.execute(
                    beginCommerceCheckoutOperation,
                    {},
                    options,
                    "checkout-operation-failed",
                    "Teqtank Commerce could not begin Checkout.",
                )
            ).checkout,
        );
    }
    public async get(options: CommerceOperationOptions): Promise<CommerceCheckout | null> {
        const value = (
            await this.execute(
                getCommerceCheckoutOperation,
                {},
                options,
                "checkout-operation-failed",
                "Teqtank Commerce could not load Checkout.",
            )
        ).checkout;
        return value === null ? null : resolveCommerceCheckout(value);
    }
    public async clearCustomerDetails(
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCheckout> {
        const request = expectedVersion === undefined ? {} : { expectedVersion };
        return resolveCommerceCheckout(
            (
                await this.execute(
                    clearCommerceCheckoutCustomerDetailsOperation,
                    request,
                    options,
                    "checkout-operation-failed",
                    "Teqtank Commerce could not clear Checkout customer details.",
                )
            ).checkout,
        );
    }

    public async updateContact(
        contact: CommerceCheckoutContact,
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCheckout> {
        const request = {
            contact,
            ...(expectedVersion !== undefined ? { expectedVersion } : {}),
        };
        return resolveCommerceCheckout(
            (
                await this.execute(
                    updateCommerceCheckoutContactOperation,
                    request,
                    options,
                    "checkout-operation-failed",
                    "Teqtank Commerce could not update Checkout contact.",
                )
            ).checkout,
        );
    }
    public async updateAddress(
        address: CommerceAddress,
        addressType: "shipping" | "billing",
        billingAddressSameAsShipping: boolean | undefined,
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCheckout> {
        const request = {
            address,
            addressType,
            ...(billingAddressSameAsShipping !== undefined ? { billingAddressSameAsShipping } : {}),
            ...(expectedVersion !== undefined ? { expectedVersion } : {}),
        };
        return resolveCommerceCheckout(
            (
                await this.execute(
                    updateCommerceCheckoutAddressOperation,
                    request,
                    options,
                    "checkout-operation-failed",
                    "Teqtank Commerce could not update Checkout address.",
                )
            ).checkout,
        );
    }
    public async selectShipping(
        shippingMethodId: string,
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCheckout> {
        const request = {
            shippingMethodId,
            ...(expectedVersion !== undefined ? { expectedVersion } : {}),
        };
        return resolveCommerceCheckout(
            (
                await this.execute(
                    selectCommerceCheckoutShippingMethodOperation,
                    request,
                    options,
                    "checkout-operation-failed",
                    "Teqtank Commerce could not select the delivery method.",
                )
            ).checkout,
        );
    }
    public async applyCustomer(
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCheckout> {
        const request = expectedVersion === undefined ? {} : { expectedVersion };
        return resolveCommerceCheckout(
            (
                await this.execute(
                    applyCommerceCheckoutCustomerOperation,
                    request,
                    options,
                    "checkout-operation-failed",
                    "Teqtank Commerce could not apply the customer account to Checkout.",
                )
            ).checkout,
        );
    }
    public async selectSavedPaymentMethod(
        paymentMethodId: string | null,
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCheckout> {
        const request = {
            paymentMethodId,
            ...(expectedVersion !== undefined ? { expectedVersion } : {}),
        };
        return resolveCommerceCheckout(
            (
                await this.execute(
                    selectCommerceCheckoutSavedPaymentMethodOperation,
                    request,
                    options,
                    "checkout-operation-failed",
                    "Teqtank Commerce could not select the saved payment method.",
                )
            ).checkout,
        );
    }
    public async getPaymentConfiguration(
        options: CommerceOperationOptions,
    ): Promise<CommerceCheckoutPaymentConfiguration> {
        return (
            await this.execute(
                getCommerceCheckoutPaymentConfigurationOperation,
                {},
                options,
                "checkout-operation-failed",
                "Teqtank Commerce could not load payment options.",
            )
        ).payment;
    }
    public async selectPaymentOption(
        paymentOptionId: string | null,
        savedPaymentMethodId: string | null,
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ) {
        const request = {
            paymentOptionId,
            savedPaymentMethodId,
            ...(expectedVersion !== undefined ? { expectedVersion } : {}),
        };
        return this.execute(
            selectCommerceCheckoutPaymentOptionOperation,
            request,
            options,
            "checkout-operation-failed",
            "Teqtank Commerce could not configure the secure payment form.",
        );
    }
    public async applyPoints(
        apply: boolean,
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ) {
        const request = { apply, ...(expectedVersion !== undefined ? { expectedVersion } : {}) };
        return this.execute(
            applyCommerceCheckoutPointsOperation,
            request,
            options,
            "checkout-operation-failed",
            "Teqtank Commerce could not update points.",
        );
    }
    public async calculate(
        expectedVersion: number | undefined,
        bypassAddressValidation: boolean,
        options: CommerceOperationOptions,
    ): Promise<CommerceCheckout> {
        const request = {
            ...(expectedVersion === undefined ? {} : { expectedVersion }),
            ...(bypassAddressValidation ? { bypassAddressValidation: true } : {}),
        };
        return resolveCommerceCheckout(
            (
                await this.execute(
                    calculateCommerceCheckoutOperation,
                    request,
                    options,
                    "checkout-operation-failed",
                    "Teqtank Commerce could not calculate Checkout.",
                )
            ).checkout,
        );
    }
    public async respondToUpsell(
        upsellId: string,
        accepted: boolean,
        choiceId: string | undefined,
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCheckout> {
        const request = {
            upsellId,
            accepted,
            ...(choiceId !== undefined ? { choiceId } : {}),
            ...(expectedVersion !== undefined ? { expectedVersion } : {}),
        };
        return resolveCommerceCheckout(
            (
                await this.execute(
                    respondCommerceCheckoutUpsellOperation,
                    request,
                    options,
                    "checkout-operation-failed",
                    "Teqtank Commerce could not respond to the Checkout upsell.",
                )
            ).checkout,
        );
    }
    public async updateItemPurchase(
        lineId: string,
        purchaseType: "one-time" | "recurring",
        frequencyId: string | undefined,
        frequencyDays: number | undefined,
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCheckout> {
        const request = {
            lineId,
            purchaseType,
            ...(frequencyId !== undefined ? { frequencyId } : {}),
            ...(frequencyDays !== undefined ? { frequencyDays } : {}),
            ...(expectedVersion !== undefined ? { expectedVersion } : {}),
        };
        return resolveCommerceCheckout(
            (
                await this.execute(
                    updateCommerceCheckoutItemPurchaseOperation,
                    request,
                    options,
                    "checkout-operation-failed",
                    "Teqtank Commerce could not update the Checkout purchase option.",
                )
            ).checkout,
        );
    }
    public async submit(
        expectedVersion: number | undefined,
        consents: readonly import("@teqtank/commerce-contracts").CommerceCustomerConsentSelection[],
        customFields: Readonly<Record<string, string>>,
        options: CommerceOperationOptions,
    ): Promise<SubmitCommerceCheckoutResponse> {
        const request = {
            consents,
            customFields,
            ...(expectedVersion === undefined ? {} : { expectedVersion }),
        };
        const response = await this.execute(
            submitCommerceCheckoutOperation,
            request,
            options,
            "checkout-operation-failed",
            "Checkout could not prepare the order for payment.",
        );
        return Object.freeze({
            ...response,
            checkout: resolveCommerceCheckout(response.checkout),
        });
    }
    public async complete(
        attemptId: string,
        paymentResult: unknown,
        options: CommerceOperationOptions,
    ): Promise<CompleteCommerceCheckoutResponse> {
        const response = await this.execute(
            completeCommerceCheckoutOperation,
            { attemptId, paymentResult },
            options,
            "payment-operation-failed",
            "Payment completed, but the order could not be finalised.",
        );
        return Object.freeze({
            ...response,
            order: resolveCommerceOrder(response.order),
            warnings: Object.freeze([...response.warnings]),
        });
    }
}
